import { Component, HostListener, Input, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { Language, TranslationService } from '../../../core/services/i18n/translation.service';

@Component({
  selector: 'app-language-switcher',
  standalone: true,
  imports: [CommonModule],
  host: { class: 'language-menu-container relative' },
  template: `
    @if (variant === 'inline') {
      <span class="mb-2 block text-xs font-semibold uppercase tracking-[0.5px] text-gray-400">
        {{ t('nav.language') }}
      </span>
      <div class="flex gap-2">
        <button
          type="button"
          (click)="changeLanguage('en')"
          class="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-md px-2.5 py-2.5 text-sm transition-all duration-200 ease-in-out [&_i]:text-sm"
          [ngClass]="
            currentLanguage() === 'en'
              ? 'border border-accent bg-accent text-black'
              : 'border border-white/10 bg-white/[0.05] text-gray-400 hover:bg-white/[0.08] hover:text-white'
          "
        >
          <i class="fas fa-flag-usa"></i>{{ t('nav.english') }}
        </button>
        <button
          type="button"
          (click)="changeLanguage('es')"
          class="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-md px-2.5 py-2.5 text-sm transition-all duration-200 ease-in-out [&_i]:text-sm"
          [ngClass]="
            currentLanguage() === 'es'
              ? 'border border-accent bg-accent text-black'
              : 'border border-white/10 bg-white/[0.05] text-gray-400 hover:bg-white/[0.08] hover:text-white'
          "
        >
          <i class="fas fa-flag"></i>{{ t('nav.spanish') }}
        </button>
      </div>
    } @else {
      <button
        type="button"
        class="relative flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-[15px] transition-all duration-200 ease-in-out"
        [ngClass]="
          tone === 'light'
            ? 'text-gray-500 hover:bg-gray-50 hover:text-accent'
            : 'text-gray-400 hover:bg-white/[0.08] hover:text-white'
        "
        (click)="toggle($event)"
        [title]="t('nav.language')"
      >
        <i class="fas fa-globe"></i>
      </button>
      @if (open()) {
        <div
          class="absolute right-0 top-[calc(100%+0.5rem)] z-[1200] min-w-[180px] rounded-lg bg-white py-2 shadow-[0_4px_20px_rgba(0,0,0,0.15)]"
        >
          <button
            type="button"
            (click)="changeLanguage('en')"
            class="flex w-full cursor-pointer items-center gap-3 border-0 px-4 py-2.5 text-left text-sm no-underline transition-colors duration-200 ease-in-out [&_i]:w-4 [&_i]:text-center [&_i]:text-sm"
            [ngClass]="
              currentLanguage() === 'en'
                ? 'bg-accent text-black'
                : 'bg-transparent text-gray-700 hover:bg-gray-100'
            "
          >
            <i class="fas fa-flag-usa"></i>{{ t('nav.english') }}
          </button>
          <button
            type="button"
            (click)="changeLanguage('es')"
            class="flex w-full cursor-pointer items-center gap-3 border-0 px-4 py-2.5 text-left text-sm no-underline transition-colors duration-200 ease-in-out [&_i]:w-4 [&_i]:text-center [&_i]:text-sm"
            [ngClass]="
              currentLanguage() === 'es'
                ? 'bg-accent text-black'
                : 'bg-transparent text-gray-700 hover:bg-gray-100'
            "
          >
            <i class="fas fa-flag"></i>{{ t('nav.spanish') }}
          </button>
        </div>
      }
    }
  `,
})
export class LanguageSwitcherComponent {
  @Input() variant: 'dropdown' | 'inline' = 'dropdown';
  @Input() tone: 'dark' | 'light' = 'dark';

  private translationService = inject(TranslationService);
  private router = inject(Router);

  open = signal(false);

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => this.open.set(false));
  }

  currentLanguage(): Language {
    return this.translationService.getCurrentLanguage()();
  }

  t(key: string): string {
    return this.translationService.t(key);
  }

  toggle(event: MouseEvent): void {
    event.stopPropagation();
    this.open.update((value) => !value);
  }

  changeLanguage(lang: Language): void {
    this.translationService.setLanguage(lang);
    this.open.set(false);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open()) return;
    const target = event.target as HTMLElement;
    if (!target.closest('.language-menu-container')) {
      this.open.set(false);
    }
  }
}
